import React, {createContext, useContext, useEffect, useMemo, useRef, useState} from "react";
import {io, Socket} from "socket.io-client";

import {fetchOnlineUsers} from "../api/presence";
import {getToken, isAuthenticated} from "../data/storage";
import OnlineUser from "../types/OnlineUser";

// Application level ping. socket.io has its own transport ping; this one keeps
// last_seen_at fresh on the server while a tab stays open.
const HEARTBEAT_MS = 25_000;

type PresenceContextValue = {
    onlineUserIds: Set<number>
    isOnline: (userId: number) => boolean
    connected: boolean
};

const PresenceContext = createContext<PresenceContextValue>({
    onlineUserIds: new Set<number>(),
    isOnline: () => false,
    connected: false
});

// REACT_APP_BACKEND_URL points at ".../api"; the socket endpoint lives under the
// same prefix so it is proxied the same way.
const getSocketTarget = (): { origin: string, path: string } => {
    const backendUrl = new URL(process.env.REACT_APP_BACKEND_URL as string, window.location.origin);
    const prefix = backendUrl.pathname.replace(/\/$/, "");
    return {origin: backendUrl.origin, path: `${prefix}/socket.io`};
};

export const PresenceProvider = ({children}: { children: React.ReactNode }): JSX.Element => {
    const [onlineUserIds, setOnlineUserIds] = useState<Set<number>>(new Set());
    const [connected, setConnected] = useState(false);
    const socketRef = useRef<Socket | null>(null);

    useEffect(() => {
        if (!isAuthenticated()) return;

        const {origin, path} = getSocketTarget();
        const socket: Socket = io(origin, {
            path,
            auth: {token: getToken()},
            transports: ["polling", "websocket"]
        });
        socketRef.current = socket;

        // Snapshot first, so the list is populated without waiting for a broadcast.
        fetchOnlineUsers().then(response => {
            if (!response) return;
            setOnlineUserIds(prev => {
                const next = new Set(prev);
                response.users.forEach((user: OnlineUser) => next.add(user.userId));
                return next;
            });
        });

        socket.on("connect", () => setConnected(true));
        socket.on("disconnect", () => setConnected(false));
        socket.on("connect_error", (error: Error) =>
            console.warn(`Presence socket could not connect to ${origin}${path}:`, error.message));

        socket.on("user:online", ({userId}: { userId: number }) =>
            setOnlineUserIds(prev => {
                if (prev.has(userId)) return prev;
                const next = new Set(prev);
                next.add(userId);
                return next;
            }));

        socket.on("user:offline", ({userId}: { userId: number }) =>
            setOnlineUserIds(prev => {
                if (!prev.has(userId)) return prev;
                const next = new Set(prev);
                next.delete(userId);
                return next;
            }));

        const heartbeat = setInterval(() => {
            if (socket.connected) socket.emit("presence:heartbeat");
        }, HEARTBEAT_MS);

        return () => {
            clearInterval(heartbeat);
            socket.removeAllListeners();
            socket.disconnect();
            socketRef.current = null;
        };
    }, []);

    const value = useMemo<PresenceContextValue>(() => ({
        onlineUserIds,
        isOnline: (userId: number) => onlineUserIds.has(userId),
        connected
    }), [onlineUserIds, connected]);

    return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
};

export const usePresence = (): PresenceContextValue => useContext(PresenceContext);

export default PresenceContext;
